
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { LastUpdate } from '../_models/lastupdate';
import { environment } from 'src/environments/environment';

class Cache {

    constructor(
        private _ws: WebServicesService
    ) { }

    private _getData(id: string) {
        return JSON.parse(sessionStorage.getItem('GG_C_' + id + '_D'));
    }

    private _getLastUpdate(id: string) {
        return new Date(JSON.parse(sessionStorage.getItem('GG_C_' + id + '_L')));
    }

    private _setData(id: string, value) {
        sessionStorage.setItem('GG_C_' + id + '_D', JSON.stringify(value));
    }

    private _setLastUpdate(id: string, value) {
        sessionStorage.setItem('GG_C_' + id + '_L', JSON.stringify(value));
    }

    public get(id: string): Observable<CacheData> {
        return this._ws.getLastUpdate(id).pipe(map(serverLastUpdate => {
            if (!serverLastUpdate || !serverLastUpdate.value) {
                return null;
            }

            const localLastUpdate = this._getLastUpdate(id);

            if (!localLastUpdate) {
                return {
                    lastUpdate: serverLastUpdate.value
                };
            }


            if (localLastUpdate.getTime() < serverLastUpdate.value.getTime()) {
                this.clear(id);

                return {
                    lastUpdate: serverLastUpdate.value
                };
            }

            return {
                data: this._getData(id),
                lastUpdate: serverLastUpdate.value
            };
        }));
    }


    public set(id: string, lastUpdate: Date, data) {
        this._setData(id, data);
        this._setLastUpdate(id, lastUpdate);
    }

    public clear(id: string) {
        this._setData(id, null);
        this._setLastUpdate(id, null);
    }
}

interface CacheData {
    data?: any;
    lastUpdate: Date;
}

@Injectable()
export class WebServicesService {
    private cache = new Cache(this);

    private postPutDeleteCallback = () => { };

    constructor(
        private _http: HttpClient,
    ) { }

    private _get<T>(url: string, saveToCache: boolean, cacheKey: string, cacheLastUpdate: Date): Observable<T> {

        return this._http.get<T>(url).pipe(map(res => {
            // saving to cache if necessary
            if (saveToCache) {
                this.cache.set(cacheKey, cacheLastUpdate, res);
                console.log('%s ** -> save to cache with %s ', cacheKey, cacheLastUpdate);
            }
            return res;
        }));

    }


    public getWithoutCache( url: string): Observable<any> {
        return this._get(url, false, null, null);
    }

    private _getWithCache<T>(url: string, cacheKey: string): Observable<T> {

        const s = new Subject<T>();

        this.cache.get(cacheKey).subscribe(res => {

            if (res) {
                if (res.data) {
                    s.next(res.data);
                    s.complete();
                } else {
                    console.log('CACHE MISS: %s', cacheKey);
                    this._get<T>(url, true, cacheKey, res.lastUpdate).subscribe(gotRes => {
                        console.log('%s ** -> got from server', cacheKey);
                        s.next(gotRes);
                        s.complete();
                    });
                }
            } else {
                console.warn('CACHE NO KEY: %s ', cacheKey);
                this._get<T>(url, false, null, null).subscribe(gotRes => {

                    s.next(gotRes);
                    s.complete();
                });
            }

        });

        return s.asObservable();
    }

    public getBlob(url: string) {
        return this._http.get(url, {responseType: 'blob' as const});
    }

    public postAndGetBlob( url: string) {
        return this._http.post(url, {}, {responseType: 'blob' as const});
    }

    public putAndGetBlob(url: string) {
        return this._http.put(url, {}, {responseType: 'blob' as const});
    }

    public postCsvContent(url: string, payload: any) {
        return this._http.post(url, payload, { headers: {'Accept': 'text/csv'}, responseType: 'text' });
    }

    public getCsvContent(url: string) {
        return this._http.get(url, { headers: {'Accept': 'text/csv'}, responseType: 'text' });
    }

    public getWithCache<T>(url: string, cacheKey: string) {
        return this._getWithCache<T>(url, cacheKey);
    }

    public putForm(url: string, data) {
        return this.submitFormHttp(url, data, 'PUT');
    }

    public postForm(url: string, data) {
        return this.submitFormHttp(url, data, 'POST');
    }

    public putJson(url: string, data) {
        return this.jsonFormHttp(url, data, 'PUT');
    }

    public postJson<T>(url: string, data) {
        return this.jsonFormHttp<T>(url, data, 'POST');
    }

    public postCsv(url: string, data) {
        return this.jsonFormHttp(url, data, 'POST', 'text/csv');
    }

    public deleteHttp(url: string) {
        return this._http.delete(url).pipe(map(res => {
            this.postPutDeleteCallback();
            return res;
        }));
    }

    private jsonFormHttp<T>(url: string, payload, method: string, contentType?: string) {
        const headers = {'Content-Type': contentType || 'application/json'};

        if (method === 'POST') {
            return this._http.post<T>(url, payload, { headers: headers }).pipe(
                
                map(res => {
                    this.postPutDeleteCallback();
                    return res;
                }));
        } else if (method === 'PUT') {
            return this._http.put<T>(url, payload, { headers: headers }).pipe(
                map(res => {
                    this.postPutDeleteCallback();
                    return res;
                }));
        }
    }

    private submitFormHttp(url: string, data, method: string) {

        const headers = {'Content-Type': 'application/x-www-form-urlencoded'};

        let payload = '';

        for (const k in data) {
            if (data.hasOwnProperty(k)) {
                if ('' === payload) {
                    payload = k + '=' + data[k];
                } else {
                    payload = payload + '&' + k + '=' + data[k];
                }
            }
        }

        payload = encodeURI(payload);

        if (method === 'POST') {
            return this._http.post(url, payload, { headers: headers }).pipe(

                map(res => {
                    this.postPutDeleteCallback();
                    return res;
                }));
        } else if (method === 'PUT') {
            return this._http.put(url, payload, { headers: headers }).pipe(
                map(res => {
                    this.postPutDeleteCallback();
                    return res;
                }));
        }
    }

    public invalidateCache(id: string) {
        this.cache.clear(id);
    }

    public getLastUpdate(id: string): Observable<LastUpdate> {
        return this.getWithoutCache(`${environment.wsBaseUrl}/api/LastUpdate/${encodeURIComponent(id)}`).pipe(
        map(e => {
            if (!e) {
                return null;
            }

            e.value = new Date(e.value);

            return e;
        }));
    }
}
